"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { createPortal } from "react-dom";
import { getSlabsByRegime } from "@/constants/taxSlabs";

interface TaxSlabDropdownProps {
	value: string;
	regime: "new" | "old";
	onChange: (value: string) => void;
	onRegimeChange: (regime: "new" | "old") => void;
	/** Renders dropdown panel inline (pushes content down) instead of absolute. Use inside drawers/modals. */
	inline?: boolean;
	/** Shows a bottom sheet on mobile instead of a regular dropdown. */
	mobileBottomSheet?: boolean;
}

const SlabList = ({
	value,
	regime,
	onChange,
	onRegimeChange,
	onClose,
}: {
	value: string;
	regime: "new" | "old";
	onChange: (v: string) => void;
	onRegimeChange: (r: "new" | "old") => void;
	onClose: () => void;
}) => {
	const slabs = getSlabsByRegime(regime);
	return (
		<>
			{/* Sticky regime toggle header */}
			<div className="flex items-center justify-between px-4 py-2.5 sticky top-0 bg-white z-10 border-b border-gray-200 rounded-t-2xl">
				<span className="text-sm md:text-base text-automint-black">
					Tax slabs &amp; rate
				</span>
				<div className="flex items-center gap-2 border border-gray-300 rounded-full py-1.5 px-2 w-fit">
					<div className="flex items-center gap-1">
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onRegimeChange("old");
							}}
							disabled={regime === "old"}
							className={
								regime === "old"
									? "opacity-30 cursor-not-allowed"
									: "cursor-pointer"
							}
						>
							<Image
								src="/new_icons/general/chevron-left.svg"
								width={20}
								height={20}
								alt="previous"
							/>
						</button>
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onRegimeChange("new");
							}}
							disabled={regime === "new"}
							className={
								regime === "new"
									? "opacity-30 cursor-not-allowed"
									: "cursor-pointer"
							}
						>
							<Image
								src="/new_icons/general/chevron-right.svg"
								width={20}
								height={20}
								alt="next"
							/>
						</button>
					</div>
					<span className="text-[11px] text-text-two pr-1 whitespace-nowrap">
						{regime === "new" ? "New Regime" : "Old Regime"}
					</span>
				</div>
			</div>

			{/* Slab rows */}
			{slabs.map((slab, idx) => (
				<div
					key={slab.value}
					onClick={() => {
						onChange(slab.value.toString());
						onClose();
					}}
					className={
						"cursor-pointer px-4 py-2.5 flex justify-between items-center text-sm text-automint-black hover:bg-gray-50 transition-colors " +
						(idx < slabs.length - 1 ? "border-b border-gray-100 " : "") +
						(value === slab.value.toString()
							? "bg-[var(--colors-automint-neutral-100)]"
							: "")
					}
				>
					<span>{slab.label}</span>
					<span>{slab.rate}</span>
				</div>
			))}
		</>
	);
};

const TaxSlabDropdown = ({
	value,
	regime,
	onChange,
	onRegimeChange,
	inline = false,
	mobileBottomSheet = false,
}: TaxSlabDropdownProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	const portalRef = useRef<HTMLDivElement>(null);
	const slabs = getSlabsByRegime(regime);

	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			const inTrigger = ref.current?.contains(e.target as Node);
			const inPortal = portalRef.current?.contains(e.target as Node);
			if (!inTrigger && !inPortal) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, []);

	const selectedSlab = slabs.find((s) => s.value.toString() === value);
	const displayLabel = selectedSlab
		? `${selectedSlab.label} - ${selectedSlab.rate}`
		: "Select an option";

	const mobileModal =
		mobileBottomSheet && isOpen && typeof document !== "undefined"
			? createPortal(
					<>
						{/* Blur backdrop */}
						<div
							className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 md:hidden"
							onClick={() => setIsOpen(false)}
						/>
						{/* Bottom sheet */}
						<div
							ref={portalRef}
							className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 md:hidden"
						>
							<div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
								<span className="text-sm md:text-base  text-automint-black">
									Tax bracket
								</span>
								<button type="button" onClick={() => setIsOpen(false)}>
									<Image
										src="/new_icons/general/close.svg"
										width={25}
										height={25}
										alt="close"
									/>
								</button>
							</div>
							<div
								className="max-h-[60vh] overflow-y-auto"
								style={{ colorScheme: "light" }}
							>
								<SlabList
									value={value}
									regime={regime}
									onChange={onChange}
									onRegimeChange={onRegimeChange}
									onClose={() => setIsOpen(false)}
								/>
							</div>
						</div>
					</>,
					document.body,
				)
			: null;

	return (
		<div ref={ref} className="relative w-full">
			<button
				type="button"
				className="w-full h-14 px-4 flex items-center rounded-2xl justify-between border border-gray-300 bg-white text-left"
				onClick={() => setIsOpen((prev) => !prev)}
			>
				<span className="text-automint-black text-sm">{displayLabel}</span>
				<Image
					src="/new_icons/general/chevron-down.svg"
					alt="chevron down"
					width={21}
					height={21}
					className="flex-shrink-0"
				/>
			</button>

			{/* Mobile: bottom sheet via portal */}
			{mobileModal}

			{/* Desktop: inline or absolute dropdown */}
			{isOpen && (
				<div
					className={`${mobileBottomSheet ? "hidden md:block" : ""} ${inline ? "" : "absolute left-0 right-0 top-full"} mt-1 rounded-2xl shadow-2xl overflow-hidden z-50`}
				>
					<div
						className="max-h-[230px] overflow-y-auto bg-white"
						style={{ colorScheme: "light" }}
					>
						<SlabList
							value={value}
							regime={regime}
							onChange={onChange}
							onRegimeChange={onRegimeChange}
							onClose={() => setIsOpen(false)}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export default TaxSlabDropdown;
